/**
 * userInfoCache.js - Shared user info caching utility
 *
 * Used by comments$.js and highlights$.js to avoid duplicate code and
 * share a single cache for user info lookups.
 */

import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';

// Shared cache for user info (to avoid repeated lookups)
const userInfoCache = new Map();

/**
 * Default user info when lookup fails or user not found
 */
const DEFAULT_USER_INFO = { username: 'User', avatar_url: null };

/**
 * Extract user info from raw Supabase user data
 * @param {Object} userData - Raw user data from Supabase
 * @returns {Object} { username, avatar_url }
 */
const extractUserInfo = (userData) => {
  if (!userData) return DEFAULT_USER_INFO;

  const meta = userData.raw_user_meta_data || {};
  return {
    username: meta.username || meta.name || userData.email?.split('@')[0] || 'User',
    avatar_url: meta.avatar_url || null,
  };
};

/**
 * Get user info for a user ID
 * Returns { username, avatar_url } from cache or fetches from Supabase
 *
 * @param {string} userId - The user ID to look up
 * @returns {Promise<{username: string, avatar_url: string|null}>}
 */
export const getUserInfo = async (userId) => {
  if (!userId) return null;

  // Check cache first
  if (userInfoCache.has(userId)) {
    return userInfoCache.get(userId);
  }

  // Fetch from Supabase using RPC
  try {
    const { data, error } = await supabase.rpc('get_user_info', { p_user_id: userId });

    if (error || !data || data.length === 0) {
      // Fallback - cache the default to avoid repeated failed lookups
      userInfoCache.set(userId, DEFAULT_USER_INFO);
      return DEFAULT_USER_INFO;
    }

    const info = extractUserInfo(data[0]);
    userInfoCache.set(userId, info);
    return info;
  } catch (err) {
    console.error('Failed to get user info:', err);
    return DEFAULT_USER_INFO;
  }
};

/**
 * Check if an entity belongs to the current user
 * Works with any entity that has a user_id field (comments, highlights, etc.)
 *
 * @param {Object} entity - The entity to check
 * @returns {boolean}
 */
export const isOwnEntity = (entity) => {
  const currentUserId = auth$.user.get()?.id;
  if (!entity?.user_id || !currentUserId) return false;
  // Use string comparison to handle potential type mismatches (UUID vs string)
  return String(entity.user_id) === String(currentUserId);
};

/**
 * Get current user ID
 * @returns {string|undefined}
 */
export const getCurrentUserId = () => {
  return auth$.user.get()?.id;
};

/**
 * Synchronously read from cache (no fetch).
 * Returns cached { username, avatar_url } or null if not yet loaded.
 */
export const getCachedUserInfo = (userId) => {
  return userId && userInfoCache.has(userId) ? userInfoCache.get(userId) : null;
};

/**
 * Clear the user info cache (useful for testing or logout)
 */
export const clearUserInfoCache = () => {
  userInfoCache.clear();
};

/**
 * Invalidate a single user's cached info (e.g. after avatar update)
 * @param {string} userId
 */
export const invalidateUserInfo = (userId) => {
  if (userId) userInfoCache.delete(userId);
};

/**
 * Prefetch user info for multiple users at once
 * Useful when displaying a list of items from different users
 *
 * @param {string[]} userIds - Array of user IDs to prefetch
 * @returns {Promise<void>}
 */
export const prefetchUserInfoBatch = async (userIds) => {
  // Filter to only IDs not already in cache
  const uncachedIds = [...new Set(userIds)].filter(id => id && !userInfoCache.has(id));

  if (uncachedIds.length === 0) return;

  // Fetch in parallel (with reasonable limit)
  const batchSize = 10;
  for (let i = 0; i < uncachedIds.length; i += batchSize) {
    const batch = uncachedIds.slice(i, i + batchSize);
    await Promise.all(batch.map(id => getUserInfo(id)));
  }
};

export default {
  getUserInfo,
  getCachedUserInfo,
  isOwnEntity,
  getCurrentUserId,
  clearUserInfoCache,
  prefetchUserInfoBatch,
};
